class CustomSelect extends HTMLElement {
  static get observedAttributes() {
    return ["selected"];
  }

  constructor() {
    super();

    this.referenceElement = document.getElementById(this.dataset.referenceId);
    this.currentLabel = this.querySelector("[ custom-select-current]");
    this.mainTrigger = this.querySelector("[custom-select-trigger]");
    this.drawer = this.querySelector("[custom-select-drawer]");
    this.handleClick = this.handleClick.bind(this);
    this.handleTriggerKeydown = this.handleTriggerKeydown.bind(this);
    this.handleListKeydown = this.handleListKeydown.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  disconectedCallback() {
    // this.removeEventListener('wtSelectChange');
  }

  attributeChangedCallback(name, oldValue, newValue) {
    const onChangeEvent = new CustomEvent("wtSelectChange", {
      detail: {
        prevValue: oldValue,
        value: newValue,
      },
    });
    this.dispatchEvent(onChangeEvent);
  }

  getListItems() {
    return Array.from(this.querySelectorAll("[custom-select-list] li"));
  }

  getSelectedOption() {
    return (
      this.querySelector("[custom-select-list] [selected]") ||
      this.getListItems()[0]
    );
  }

  getFocusableElements() {
    const focusableElements = this.getListItems().filter(
      (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
    );

    return {
      focusableElements,
      first: focusableElements[0],
      last: focusableElements[focusableElements.length - 1],
    };
  }

  setOptionTabindexes(focusedOption = null) {
    const isOpen = this.hasAttribute("open");

    this.getListItems().forEach((item) => {
      if (!isOpen) {
        item.setAttribute("tabindex", "-1");
      } else {
        item.setAttribute("tabindex", item === focusedOption ? "0" : "-1");
      }
    });
  }

  focusOption(option) {
    if (!option) return;
    this.setOptionTabindexes(option);
    requestAnimationFrame(() => {
      option.focus({ preventScroll: true });
    });
  }

  focusTrigger() {
    requestAnimationFrame(() => {
      this.mainTrigger?.focus({ preventScroll: true });
    });
  }

  updateDrawerAccessibility(isAccessible) {
    if (!this.drawer) return;

    if (isAccessible) {
      this.drawer.removeAttribute("aria-hidden");
      this.drawer.inert = false;
    } else {
      this.drawer.setAttribute("aria-hidden", "true");
      this.drawer.inert = true;
    }
  }

  setSelectedLabel() {
    const selectedLabel = this.querySelector(
      "[custom-select-list] [selected]",
    )?.textContent;

    this.setAttribute("selected-label", selectedLabel);
    this.currentLabel.textContent = selectedLabel;
  }

  getAttrVal(attr, elem) {
    return elem.hasAttribute(attr) ? elem.getAttribute(attr) : null;
  }

  unselectAll() {
    this.getListItems().forEach((item) => {
      item.removeAttribute("selected");
      item.setAttribute("aria-selected", "false");
    });
  }

  onSelect(val, elem) {
    this.unselectAll();
    this.setAttribute("selected", val);
    elem.setAttribute("selected", "");
    elem.setAttribute("aria-selected", "true");
    this.setSelectedLabel();
    this.updateReferenceValue();
    this.toggleState();
  }

  createOverlay() {
    if (!document.querySelector("[custom-select-overlay]")) {
      const overlay = document.createElement("div");
      overlay.setAttribute("custom-select-overlay", "");
      document.body.appendChild(overlay);
      overlay.addEventListener("click", () => this.toggleState());
    }
  }

  handleTriggerKeydown(e) {
    if (this.hasAttribute("open")) return;

    const key = e.key;

    if (key === " " || key === "Spacebar") {
      e.preventDefault();
      this.toggleState();
      return;
    }

    if (key === "Enter") {
      e.preventDefault();
      this.toggleState();
      return;
    }

    if (key === "ArrowDown" || key === "ArrowUp") {
      e.preventDefault();
      this.toggleState();
    }
  }

  handleListKeydown(e) {
    if (!this.hasAttribute("open")) return;

    const items = this.getListItems();
    const currentIndex = items.indexOf(document.activeElement);
    const key = e.key;

    if (key === "Escape") {
      e.preventDefault();
      this.toggleState();
      return;
    }

    if (key === " " || key === "Spacebar" || key === "Enter") {
      e.preventDefault();
      const option = document.activeElement;
      if (option?.hasAttribute("value")) {
        this.onSelect(this.getAttrVal("value", option), option);
      }
      return;
    }

    let nextIndex = -1;

    if (key === "ArrowDown") {
      e.preventDefault();
      nextIndex =
        currentIndex < 0 ? 0 : (currentIndex + 1) % items.length;
    } else if (key === "ArrowUp") {
      e.preventDefault();
      nextIndex =
        currentIndex <= 0 ? items.length - 1 : currentIndex - 1;
    } else if (key === "Home") {
      e.preventDefault();
      nextIndex = 0;
    } else if (key === "End") {
      e.preventDefault();
      nextIndex = items.length - 1;
    }

    if (nextIndex >= 0 && items[nextIndex]) {
      this.focusOption(items[nextIndex]);
    }
  }

  handleClick(e) {
    const clickedElem = e.target;

    if (
      clickedElem.hasAttribute("custom-select-drawer") ||
      clickedElem === this ||
      clickedElem.classList.contains("wt-options__close")
    ) {
      this.toggleState();
    }

    if (this.getAttrVal("value", clickedElem)) {
      this.onSelect(this.getAttrVal("value", e.target), clickedElem);
    }
  }

  init() {
    this.createOverlay();
    this.mainTrigger.addEventListener("click", () => this.toggleState());
    this.mainTrigger.addEventListener("keydown", this.handleTriggerKeydown);

    const list = this.querySelector("[custom-select-list]");
    list?.addEventListener("keydown", this.handleListKeydown);

    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.hasAttribute("open")) {
          e.preventDefault();
          this.toggleState();
        }
      }

      if (isTabPressed && this.hasAttribute("open")) {
        if (!first || !last) return;

        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.addEventListener("click", this.handleClick);
    this.setSelectedLabel();
    this.setOptionTabindexes();
    this.updateDrawerAccessibility(false);
  }

  updateReferenceValue() {
    this.referenceElement.value = this.getAttribute("selected");
    const inputEvent = new Event("input", { bubbles: true, cancelable: true });
    this.referenceElement.dispatchEvent(inputEvent);
  }

  toggleState() {
    const body = document.body;
    const openBodyAttr = "custom-select-opened";
    const header = document.querySelector("header.page-header");
    const trigger = this.querySelector("[custom-select-trigger]");
    const isOpen = this.hasAttribute("open");

    trigger.setAttribute("aria-expanded", !isOpen);

    if (isOpen) {
      this.mainTrigger?.focus({ preventScroll: true });
      this.removeAttribute("open");
      body.removeAttribute(openBodyAttr);
      body.classList.contains("page-header-sticky")
        ? header.classList.remove("hide")
        : "";
      this.setOptionTabindexes();
      this.updateDrawerAccessibility(false);
    } else {
      this.setAttribute("open", "");
      body.setAttribute(openBodyAttr, "");
      body.classList.contains("page-header-sticky")
        ? header.classList.add("hide")
        : "";
      this.updateDrawerAccessibility(true);
      this.focusOption(this.getSelectedOption());
    }
  }
}

customElements.define("custom-select", CustomSelect);
